import { useEffect, useRef } from 'react';
import { useSecureAuthContext } from '@/contexts/SecureAuthContext';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

export const BackgroundAutomationRunner = () => {
  const { user } = useSecureAuthContext();
  const intervalRef = useRef<number | null>(null);
  const processingRef = useRef<boolean>(false);
  const lastRunRef = useRef<number>(0);
  const errorCountRef = useRef<number>(0);
  const errorCooldownUntilRef = useRef<number>(0);
  const processingJobsRef = useRef<Set<string>>(new Set());
  const rateLimitUntilRef = useRef<Map<string, number>>(new Map());
  useEffect(() => {
    if (!user?.id) {
      console.log('🤖 Automation Runner: No user logged in');
      return;
    }

    console.log('🤖 Automation Runner: Starting for user', user.id);

    const processRunningJobs = async () => {
      const now = Date.now();
      
      // Prevent rapid successive calls (debounce) - reduced to 5 seconds
      if (now - lastRunRef.current < 5000) {
        console.log('🤖 Automation Runner: Skipping - too soon since last run');
        return;
      }

      // Prevent concurrent processing
      if (processingRef.current) {
        console.log('🤖 Automation Runner: Already processing, skipping...');
        return;
      }

      // Circuit breaker with cooldown - increased threshold to 10
      if (errorCountRef.current >= 10) {
        const nowTs = Date.now();
        if (nowTs < errorCooldownUntilRef.current) {
          console.error('🤖 Automation Runner: Too many errors, pausing...');
          return;
        } else {
          console.log('🤖 Automation Runner: Cooldown elapsed, resuming');
          errorCountRef.current = 0;
        }
      }

      processingRef.current = true;
      lastRunRef.current = now;

      try {
        console.log('🤖 Automation Runner: Checking for running jobs...');
        
        // Get all running jobs
        const { data: jobs, error: jobsError } = await supabase
          .from('automated_generation_jobs')
          .select('*')
          .eq('status', 'running')
          .eq('created_by', user.id);

        if (jobsError) {
          console.error('🤖 Automation Runner: Error fetching jobs:', jobsError);
          throw jobsError;
        }

        // If no running jobs, check for pending jobs and auto-start the first one
        if (!jobs || jobs.length === 0) {
          console.log('🤖 Automation Runner: No running jobs found, checking for pending jobs...');
          
          const { data: pendingJobs, error: pendingError } = await supabase
            .from('automated_generation_jobs')
            .select('*')
            .eq('status', 'pending')
            .eq('created_by', user.id)
            .order('created_at', { ascending: true })
            .limit(1);

          if (pendingError) {
            console.error('🤖 Automation Runner: Error fetching pending jobs:', pendingError);
            throw pendingError;
          }

          if (pendingJobs && pendingJobs.length > 0) {
            const nextJob = pendingJobs[0];
            console.log(`🚀 Auto-starting pending job: "${nextJob.job_name}"`);
            
            const { error: startError } = await supabase
              .from('automated_generation_jobs')
              .update({ 
                status: 'running',
                start_time: new Date().toISOString()
              })
              .eq('id', nextJob.id);

            if (startError) {
              console.error('🤖 Automation Runner: Error starting pending job:', startError);
            } else {
              toast.success(`Started job: ${nextJob.job_name}`);
              // Process immediately after starting
              return;
            }
          } else {
            console.log('🤖 Automation Runner: No pending jobs found');
          }
          
          errorCountRef.current = 0; // Reset error count when successful
          return;
        }

        console.log(`🤖 Automation Runner: Found ${jobs.length} running job(s)`);

        // Process each job
        for (const job of jobs) {
          // Skip if already processing this job
          if (processingJobsRef.current.has(job.id)) {
            console.log(`🤖 Job "${job.job_name}" is already being processed, skipping...`);
            continue;
          }

          // Skip if job is already complete
          if (job.images_generated >= job.total_images_target) {
            console.log(`🤖 Job "${job.job_name}" is already complete (${job.images_generated}/${job.total_images_target}), marking as completed...`);
            // Ensure job status is updated to completed
            await supabase
              .from('automated_generation_jobs')
              .update({ status: 'completed', end_time: new Date().toISOString() })
              .eq('id', job.id);
            processingJobsRef.current.delete(job.id);
            continue;
          }

          try {
            processingJobsRef.current.add(job.id);
            console.log(`🤖 Processing job "${job.job_name}" (${job.images_generated}/${job.total_images_target})`);

            // Respect per-job rate limit backoff if set
            const until = rateLimitUntilRef.current.get(job.id);
            if (until && Date.now() < until) {
              const waitMs = until - Date.now();
              console.log(`🤖 Backing off job ${job.id} for ${Math.ceil(waitMs / 1000)}s due to prior 429`);
              continue;
            }
            
            // Call edge function to process next batch in parallel
            const { data, error } = await supabase.functions.invoke('process-automation-job', {
              body: { jobId: job.id }
            });

            if (error) {
              const anyErr: any = error;
              console.log(`🤖 Edge function error for job ${job.id}:`, anyErr);
              
              // Try multiple ways to parse the error body
              let errorBody: any = null;
              try {
                // First, check if data is present (Supabase may return data even with error)
                if (data && typeof data === 'object') {
                  errorBody = data;
                }
                // Try context.body
                else if (anyErr?.context?.body) {
                  errorBody = typeof anyErr.context.body === 'string' 
                    ? JSON.parse(anyErr.context.body) 
                    : anyErr.context.body;
                }
                // Try parsing from message if body not found
                else if (anyErr?.message) {
                  const jsonMatch = anyErr.message.match(/\{.*\}/);
                  if (jsonMatch) {
                    errorBody = JSON.parse(jsonMatch[0]);
                  }
                }
              } catch (parseError) {
                console.log('🤖 Could not parse error body:', parseError);
              }

              // Check if job was stopped/paused (not an error, just user action)
              if (errorBody?.error === 'Job is not running' || errorBody?.status === 'paused' || errorBody?.status === 'stopped' || errorBody?.status === 'cancelled') {
                console.log(`🤖 Job "${job.job_name}" is ${errorBody.status || 'not running'}, skipping automation`);
                processingJobsRef.current.delete(job.id); // Remove from processing set
                errorCountRef.current = 0; // Reset error count since this is intentional
                continue;
              }

              // Handle missing keys or global rate limit gracefully
              if (errorBody?.status === 'rate_limited_or_missing_keys') {
                errorCooldownUntilRef.current = Date.now() + 60_000; // 60s cooldown
                console.warn('🤖 Missing automation keys or provider rate-limited globally. Cooling down 60s');
                errorCountRef.current = 0;
                continue;
              }

              // Check for rate limit (wait_seconds)
              let waitSeconds: number | null = null;
              if (errorBody?.wait_seconds) {
                waitSeconds = Number(errorBody.wait_seconds);
              } else {
                const m = String(anyErr?.message || '').match(/wait_seconds"?:\s*([0-9]+)/);
                if (m) waitSeconds = parseInt(m[1], 10);
              }
              if (waitSeconds && Number.isFinite(waitSeconds)) {
                rateLimitUntilRef.current.set(job.id, Date.now() + waitSeconds * 1000);
                console.warn(`🤖 Rate limit for job ${job.id}. Will retry in ${waitSeconds}s`);
                errorCountRef.current = 0; // Reset since this is expected
              } else {
                console.error(`🤖 Unexpected error from edge function for job ${job.id}:`, error);
                errorCountRef.current++;
              }
              continue;
            }

            console.log(`🤖 Job "${job.job_name}" response:`, data);
            
            if (data?.message === 'Job completed') {
              toast.success(`Job "${job.job_name}" completed! ${data.images_generated} images generated.`);
              processingJobsRef.current.delete(job.id); // Remove from processing set
              errorCountRef.current = 0; // Reset on success
            } else if (data?.success) {
              console.log(`🤖 ✅ Generated ${data.successful} images this batch (${data.images_generated}/${data.total_target})`);
              if (data.images && data.images.length > 0) {
                console.log(`🤖 📸 Image URLs:`, data.images.map((img: any) => img.url).join(', '));
              }
              errorCountRef.current = 0; // Reset on success
            } else if (data?.message === 'Rate limit reached' || data?.status === 'rate_limited') {
              const ws = Number(data?.wait_seconds ?? 0);
              if (ws) rateLimitUntilRef.current.set(job.id, Date.now() + ws * 1000);
              console.log(`🤖 ⏳ Rate limit reached, waiting ${ws}s`);
              if (data?.details) console.log(`🤖 ${data.details}`);
              errorCountRef.current = 0; // Reset error count since this is expected
            }
          } catch (error) {
            console.error(`🤖 Error processing job ${job.id}:`, error);
            errorCountRef.current++;
          } finally {
            processingJobsRef.current.delete(job.id);
          }
        }
      } catch (error) {
        console.error('🤖 Automation Runner: Fatal error:', error);
        errorCountRef.current++;
        if (errorCountRef.current >= 5 && errorCooldownUntilRef.current < Date.now()) {
          errorCooldownUntilRef.current = Date.now() + 60_000;
          console.warn('🤖 Automation Runner: Entering cooldown for 60s after fatal errors');
        }
      } finally {
        processingRef.current = false;
        console.log('🤖 Automation Runner: Processing cycle complete');
      }
    };

    // Process immediately on mount
    console.log('🤖 Automation Runner: Running initial check...');
    processRunningJobs();

    // Then process every 18 seconds to balance speed and provider limits
    intervalRef.current = window.setInterval(() => {
      console.log('🤖 Automation Runner: Interval tick');
      processRunningJobs();
    }, 18000);

    return () => {
      console.log('🤖 Automation Runner: Cleaning up...');
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
      processingRef.current = false;
    };
  }, [user?.id]);

  return null;
};
